import { getBillingMode, selectAllowUpgrade } from '@va/aaas/shared/selectors';
import { AaasBillingMode } from '@va/aaas/shared/types';
import { startUpgradeProcess } from '@va/dashboard/actions/app';
import { CircleArrowIcon, WarningTriangleIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { Button, fontWeights, Paragraph } from '@va/ui/design-system';
import { is3asApp } from '@va/util/helpers';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAiChatbotContext } from './aiChatbotContext';

type LimitReachedWarningProps = {
  className?: string;
};

export const LimitReachedWarning = ({ className }: LimitReachedWarningProps) => {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const { quota } = useAiChatbotContext();

  const billingMode = useSelector(getBillingMode);
  const allowUpgrade = useSelector(selectAllowUpgrade);
  const isInternalUpgradeEnabled = useMemo(() => billingMode === AaasBillingMode.PlatformManaged, [billingMode]);

  const isUpgradeButtonVisible = useMemo(() => {
    if (is3asApp()) return allowUpgrade || isInternalUpgradeEnabled;

    return true;
  }, [allowUpgrade, isInternalUpgradeEnabled]);

  if (!quota?.limitReached) return null;

  return (
    <div className={classNames('rounded-6u overflow-hidden bg-white', className)}>
      <div className='flex items-center px-8u py-6u gap-6u'>
        <WarningTriangleIcon className='w-10u h-10u' color='var(--color-negative)' />
        <Paragraph
          variant='interface'
          variantSize='regular'
          colorClassName='text-negative'
          weight={fontWeights.semibold}
        >
          {translate(is3asApp() ? 'aaas.aiChatbot.limitWarning.message' : 'aiChatbot.limitWarning.message', {
            limit: quota.questionsLimit,
          })}
        </Paragraph>
      </div>
      {isUpgradeButtonVisible && (
        <Button
          className='rounded-none w-full py-7u'
          textClasses='text-interface-regular leading-interface-regular tracking-interface-regular'
          text={translate('aiChatbot.limitWarning.upgradeButton')}
          onClick={() => dispatch(startUpgradeProcess())}
          size='small'
          rightSideIcon={<CircleArrowIcon className='w-10u h-10u' orientation='up' color='#FFFFFF80' />}
          rightSideIconWrapperCss='!ml-4u border-none'
        />
      )}
    </div>
  );
};
